import { homepageEvents } from "../../models/site";
import { useHorizontalSliderPresenter } from "../../presenters/useInteractionPresenters";
import { SmartImage } from "../SmartImage";
import { ExternalLink } from "../pages/ExternalLink";

export function HomeEventsSlider() {
  const {
    viewportRef,
    trackRef,
    orderedIndexes,
    trackStyle,
    isAnimating,
    next,
    previous,
    onTransitionEnd,
    pointerDown,
    pointerMove,
    finishDrag,
    cancelDrag,
    shouldSuppressClick,
    userPaused,
    pauseForInteraction,
    resumeAfterInteraction,
    toggleAutoPlay,
  } = useHorizontalSliderPresenter(homepageEvents.length, { autoPlayMs: 5000 });

  return (
    <section className="home-events-section" aria-labelledby="events-title">
      <header className="home-events-heading">
        <div>
          <p>一年四季都精彩</p>
          <h2 id="events-title">
            EVENTS <span>台東活動</span>
          </h2>
        </div>
        <a className="text-link" href="/events">
          VIEW ALL EVENTS →
        </a>
      </header>
      <div
        className="home-events-frame"
        onMouseEnter={pauseForInteraction}
        onMouseLeave={(event) => {
          if (event.buttons === 0) resumeAfterInteraction();
        }}
        onFocusCapture={pauseForInteraction}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            resumeAfterInteraction();
        }}
      >
        <div
          className="home-events-viewport"
          ref={viewportRef}
          tabIndex={0}
          aria-label="台東活動橫向滑動列表"
          onPointerDown={(event) => {
            if (!event.isPrimary || event.button !== 0) return;
            try {
              event.currentTarget.setPointerCapture(event.pointerId);
            } catch {
              // Synthetic test events do not own an active browser pointer.
            }
            pauseForInteraction();
            pointerDown(event.clientX);
          }}
          onPointerMove={(event) => pointerMove(event.clientX)}
          onPointerUp={(event) => {
            const dragged = finishDrag();
            resumeAfterInteraction();
            try {
              event.currentTarget.releasePointerCapture(event.pointerId);
            } catch {
              // Pointer capture may already be released by the browser.
            }
            if (dragged) event.preventDefault();
          }}
          onPointerCancel={() => {
            cancelDrag();
            resumeAfterInteraction();
          }}
          onLostPointerCapture={() => {
            cancelDrag();
            resumeAfterInteraction();
          }}
          onDragStart={(event) => event.preventDefault()}
          onClickCapture={(event) => {
            if (!shouldSuppressClick()) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowLeft") {
              event.preventDefault();
              previous();
            }
            if (event.key === "ArrowRight") {
              event.preventDefault();
              next();
            }
          }}
        >
          <div
            className={`home-events-track ${isAnimating ? "is-sliding" : ""}`}
            ref={trackRef}
            style={trackStyle}
            onTransitionEnd={(event) => {
              if (event.target === event.currentTarget) onTransitionEnd();
            }}
          >
            {orderedIndexes.map((index) => {
              const event = homepageEvents[index];
              return (
                <article
                  className="home-event-card"
                  data-event-slide
                  key={event.image}
                  aria-label={`活動 ${index + 1} / ${homepageEvents.length}：${event.title}`}
                >
                  <SmartImage
                    name={event.image}
                    alt={`${event.title} ${event.en}`}
                    sizes="(max-width: 767px) 82vw, (max-width: 1024px) 46vw, 34vw"
                  />
                  <div className="home-event-copy">
                    <p className="event-date">{event.date}</p>
                    <h3>
                      {event.title}
                      <span>{event.en}</span>
                    </h3>
                    <p>{event.description}</p>
                    <ExternalLink className="text-link" href={event.href}>
                      {event.cta} ↗
                    </ExternalLink>
                    <span className="home-event-index">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
        <div className="home-events-controls">
          <button type="button" onClick={previous} aria-label="向左滑動活動">
            ←
          </button>
          <button type="button" onClick={next} aria-label="向右滑動活動">
            →
          </button>
          <button
            className="autoplay-toggle"
            type="button"
            onClick={toggleAutoPlay}
            aria-pressed={userPaused}
            aria-label={userPaused ? "開始自動滑動" : "暫停自動滑動"}
          >
            {userPaused ? "PLAY" : "PAUSE"}
          </button>
        </div>
      </div>
    </section>
  );
}
